import { AxiosResponse } from "axios";
import { useContext } from "react";
import {
  ConsolidatedInvoicesExcelRequest,
  DealerTimeLimitInvoiceLineItem,
  EXCEL_MIME_TYPE,
  Invoice,
  InvoiceHeaderDetails,
  InvoiceHeaderDetailsV2,
  InvoiceLineItem,
  InvoicesResponse,
  LineItem,
} from "./interfaces";
import { ApiContext } from "../../context/ApiContext/ApiContext";
import { ConfigContext } from "../../context/ConfigContext";
import { BlobResponse } from "../../utils/downloadFile";

const useInvoiceApi = () => {
  const axios = useContext(ApiContext);
  const { INVOICE_SERVICE_URL: invoiceServiceUrl } = useContext(ConfigContext);

  return {
    getInvoicePDF: async (
      invoiceNumber: string,
      accountId: string,
      objTypeCode?: string | null,
      location?: string,
      lang?: string,
      fileName?: string | null,
      mru?: string | null
    ): Promise<AxiosResponse<Blob>> =>
      axios
        .getWithRetries(`${invoiceServiceUrl}/v1/invoices/${invoiceNumber}`, {
          headers: {
            "account-id": accountId,
            Accept: "application/pdf",
          },
          responseType: "blob",
          params: {
            objTypeCode,
            fileName,
            lang,
            mru,
            location,
          },
        })
        .then((response) => response),
    getInvoiceAttachment: async (
      invoiceNumber: string,
      fileName: string | null,
      officialDocumentNumber: string | null,
      accountId: string,
      lang?: string
    ): Promise<AxiosResponse<Blob>> =>
      axios
        .getWithRetries(
          `${invoiceServiceUrl}/v1/invoices/${invoiceNumber}/attachment`,
          {
            headers: {
              "account-id": accountId,
            },
            responseType: "blob",
            params: {
              fileName,
              officialDocumentNumber,
              lang,
            },
          }
        )
        .then((response) => response),
    getInvoice: async (
      invoiceNumber: string,
      accountId: string,
      location?: string,
      lang?: string,
      lineItemPartDescTranslationEnabled?: boolean
    ): Promise<Invoice> => {
      const invoiceHeaderResponse = await axios
        .getWithRetries(`${invoiceServiceUrl}/v1/invoices/${invoiceNumber}`, {
          headers: {
            "account-id": accountId,
          },
          params: {
            location,
            lang,
          },
        })
        .then((response) => response.data);

      const invoiceLineItemsResponse = await axios
        .getWithRetries(
          `${invoiceServiceUrl}/v1/invoices/${invoiceNumber}/line-items`,
          {
            headers: {
              ...(lineItemPartDescTranslationEnabled
                ? { "account-id": accountId || "" }
                : {}),
            },
            params: {
              lang,
            },
          }
        )
        .then((response) =>
          response.data.lineItems
            .map(
              (lineItem: InvoiceLineItem) =>
                ({
                  serialNumber: lineItem.lineNumber,
                  invoicedPart: lineItem.partNumber,
                  partDesc: lineItem.description,
                  billingQuantity: lineItem.quantity,
                } as LineItem)
            )
            .sort(
              (lineItemA: LineItem, lineItemB: LineItem) =>
                lineItemA.serialNumber - lineItemB.serialNumber
            )
        );

      return {
        invoiceNumber: invoiceHeaderResponse.invoiceNumber,
        nfeNumber: invoiceHeaderResponse.nfeNumber,
        lineItems: invoiceLineItemsResponse,
        billingDate: invoiceHeaderResponse.invoiceDate,
      } as Invoice;
    },
    getInvoiceWithTimeLimit: async (
      invoiceNumber: string,
      accountId: string,
      transactionType: string,
      location?: string,
      lang?: string
    ): Promise<Invoice> => {
      const invoiceHeaderResponse = await axios
        .getWithRetries(`${invoiceServiceUrl}/v2/invoices/${invoiceNumber}`, {
          headers: {
            "account-id": accountId,
          },
          params: {
            location,
            lang,
          },
        })
        .then((response) => response.data);

      const invoiceLineItemsResponse = await axios
        .getWithRetries(
          `${invoiceServiceUrl}/v2/invoices/${invoiceNumber}/line-items`,
          {
            headers: {
              "account-id": accountId,
            },
            params: {
              transactionType,
              location,
            },
          }
        )
        .then((response) =>
          response.data.lineItems
            .map(
              (lineItem: InvoiceLineItem) =>
                ({
                  serialNumber: lineItem.lineNumber,
                  invoicedPart: lineItem.partNumber,
                  partDesc: lineItem.description,
                  billingQuantity: lineItem.quantity,
                  timeLimitExceeded: lineItem.timeLimitExceeded === "true",
                } as LineItem)
            )
            .sort(
              (lineItemA: LineItem, lineItemB: LineItem) =>
                lineItemA.serialNumber - lineItemB.serialNumber
            )
        );
      return {
        invoiceNumber: invoiceHeaderResponse.invoiceNumber,
        nfeNumber: invoiceHeaderResponse.nfeNumber,
        timeLimitExceeded: invoiceHeaderResponse.timeLimit?.returnsExceeded,
        lineItems: invoiceLineItemsResponse,
      } as Invoice;
    },
    getMultiInvoiceTimeLimitForLineItems: async (
      accountId: string,
      transactionType: string,
      invoiceNumbers: string
    ): Promise<DealerTimeLimitInvoiceLineItem[]> => {
      const dealerTimeLimitForMultiInvoiceResponse = await axios
        .getWithRetries(`${invoiceServiceUrl}/v2/invoices/time-limit`, {
          headers: {
            "account-id": accountId,
          },
          params: {
            invoiceNumbers,
            transactionType,
          },
        })
        .then((response) => response.data);

      return dealerTimeLimitForMultiInvoiceResponse;
    },

    downloadInvoices: async (
      accountId: string,
      jobId: string
    ): Promise<AxiosResponse<Blob>> =>
      axios
        .getWithRetries(
          `${invoiceServiceUrl}/v1/invoices/download-consolidated/${jobId}`,
          {
            responseType: "blob",
            headers: {
              "account-id": accountId,
            },
          }
        )
        .then((response) => response),
    getInvoices: async (
      accountId: string,
      lang?: string,
      pageSize?: number,
      pageNumber?: number,
      startDate?: string,
      endDate?: string,
      orderBy?: string,
      sortBy?: string,
      invoiceNumber?: string,
      documentNumber?: string,
      dealerReference?: string,
      invoiceType?: string,
      partNumber?: string,
      huNumber?: string,
      nfeNumber?: string,
      officialDocumentNumber?: string,
      withHandlingUnits?: boolean,
      withLeadingZeros?: boolean,
      dealerIdFilter?: string,
      factura?: string,
      mru?: string,
      country?: string,
      srcProcessType?: string
    ): Promise<InvoicesResponse> => {
      const response = await axios.getWithRetries(
        `${invoiceServiceUrl}/v1/invoices`,
        {
          headers: {
            "account-id": accountId,
          },
          params: {
            startDate,
            endDate,
            orderBy,
            sortBy,
            pageSize,
            pageNumber,
            lang,
            invoiceNumber,
            documentNumber,
            dealerReference,
            invoiceType,
            partNumber,
            huNumber,
            nfeNumber,
            officialDocumentNumber,
            withHandlingUnits,
            withLeadingZeros,
            dealerIdFilter,
            factura,
            mru,
            country,
            srcProcessType,
          },
        }
      );

      return {
        ...response.data,
        invoices: response.data.invoices.map((invoice: any) => ({
          ...invoice,
          documents: invoice.documents.flatMap((document: any) => {
            if (document.handlingUnits.length === 0) {
              return {
                documentNumber: document.documentNumber,
                dealerReference: document.dealerReference,
                orderType: document.orderType,
              };
            }

            return document.handlingUnits.map((handlingUnit: string) => ({
              documentNumber: document.documentNumber,
              dealerReference: document.dealerReference,
              orderType: document.orderType,
              handlingUnit,
            }));
          }),
        })),
      };
    },
    invoiceViewed: async (
      invoiceNumber: string | undefined,
      dealerId: string,
      seenBy: string,
      country: string | undefined,
      lang: string
    ): Promise<void> => {
      await axios.patchWithRetries(
        `${invoiceServiceUrl}/v1/invoices/${invoiceNumber}?lang=${lang}`,
        {
          seenBy,
          country,
        },
        {
          headers: {
            "account-id": dealerId,
          },
        }
      );
    },
    getInvoiceDetails: async (
      invoiceNumber: string,
      accountId: string,
      mru: string,
      location?: string,
      lang?: string,
      withLeadingZeros?: boolean,
      portlandSurchargesEnabled?: boolean
    ): Promise<InvoiceHeaderDetails> =>
      axios
        .getWithRetries(`${invoiceServiceUrl}/v1/invoices/${invoiceNumber}`, {
          headers: {
            "account-id": accountId,
          },
          params: {
            location,
            lang,
            withLeadingZeros,
            portlandSurchargesEnabled,
            mru,
          },
        })
        .then((response) => ({
          ...response.data,
          shipToAddress: {
            addressLine1: response.data.shipToAddress?.name,
            addressLine2: response.data.shipToAddress?.street,
            cityName: response.data.shipToAddress?.city,
            stateOrProvince: response.data.shipToAddress?.region,
            countryCode: response.data.shipToAddress?.country,
            postalOrZipCode: response.data.shipToAddress?.postCode,
            phoneNumber: response.data.shipToAddress?.telNumber,
          },
          soldToAddress: {
            addressLine1: response.data.soldToAddress?.name,
            addressLine2: response.data.soldToAddress?.street,
            cityName: response.data.soldToAddress?.city,
            stateOrProvince: response.data.soldToAddress?.region,
            countryCode: response.data.soldToAddress?.country,
            postalOrZipCode: response.data.soldToAddress?.postCode,
            phoneNumber: response.data.soldToAddress?.telNumber,
          },
          shippingInformation: {
            addressLine1: response.data.shippingInformation?.name,
            addressLine2: response.data.shippingInformation?.street,
            cityName: response.data.shippingInformation?.city,
            stateOrProvince: response.data.shippingInformation?.region,
            countryCode: response.data.shippingInformation?.country,
            postalOrZipCode: response.data.shippingInformation?.postCode,
            phoneNumber: response.data.shippingInformation?.telNumber,
          },
          invoiceDate: response.data.invoiceDate,
        })),
    getInvoiceDetailsV2: async (
      invoiceNumber: string,
      accountId: string,
      mru: string,
      location?: string,
      lang?: string,
      withLeadingZeros?: boolean,
      portlandSurchargesEnabled?: boolean
    ): Promise<InvoiceHeaderDetailsV2> =>
      axios
        .getWithRetries(`${invoiceServiceUrl}/v2/invoices/${invoiceNumber}`, {
          headers: {
            "account-id": accountId,
          },
          params: {
            location,
            lang,
            withLeadingZeros,
            portlandSurchargesEnabled,
            mru,
          },
        })
        .then((response) => ({
          ...response.data,
          shipToAddress: {
            addressLine1: response.data.shipToAddress?.name,
            addressLine2: response.data.shipToAddress?.street,
            cityName: response.data.shipToAddress?.city,
            stateOrProvince: response.data.shipToAddress?.region,
            countryCode: response.data.shipToAddress?.country,
            postalOrZipCode: response.data.shipToAddress?.postCode,
            phoneNumber: response.data.shipToAddress?.telNumber,
          },
          soldToAddress: {
            addressLine1: response.data.soldToAddress?.name,
            addressLine2: response.data.soldToAddress?.street,
            cityName: response.data.soldToAddress?.city,
            stateOrProvince: response.data.soldToAddress?.region,
            countryCode: response.data.soldToAddress?.country,
            postalOrZipCode: response.data.soldToAddress?.postCode,
            phoneNumber: response.data.soldToAddress?.telNumber,
          },
          shippingInformation: {
            addressLine1: response.data.shippingInformation?.name,
            addressLine2: response.data.shippingInformation?.street,
            cityName: response.data.shippingInformation?.city,
            stateOrProvince: response.data.shippingInformation?.region,
            countryCode: response.data.shippingInformation?.country,
            postalOrZipCode: response.data.shippingInformation?.postCode,
            phoneNumber: response.data.shippingInformation?.telNumber,
          },
          invoiceDate: response.data.invoiceDate,
        })),
    getInvoiceLineItems: async (
      invoiceNumber: string,
      location?: string,
      lang?: string,
      accountId?: string,
      lineItemPartDescTranslationEnabled?: boolean,
      portlandSurchargesEnabled?: boolean
    ): Promise<InvoiceLineItem[]> =>
      axios
        .getWithRetries(
          `${invoiceServiceUrl}/v1/invoices/${invoiceNumber}/line-items`,
          {
            headers: {
              ...(lineItemPartDescTranslationEnabled
                ? { "account-id": accountId || "" }
                : {}),
            },
            params: {
              location,
              lang,
              portlandSurchargesEnabled,
            },
          }
        )
        .then((res) =>
          res.data.lineItems.map((lineItem: InvoiceLineItem) => ({
            ...lineItem,
          }))
        ),
    getDealerTimeLimitForInvoiceLineItem: async (
      accountId: string,
      transactionType: string,
      invoiceNumber: string,
      invoiceLineNumber: string,
      returnReason?: string,
      lang?: string
    ): Promise<DealerTimeLimitInvoiceLineItem> =>
      axios
        .getWithRetries(
          `${invoiceServiceUrl}/v1/invoices/line-item/${invoiceLineNumber}/time-limit`,
          {
            headers: {
              "account-id": accountId,
            },
            params: {
              transactionType,
              invoiceNumber,
              invoiceLineNumber,
              returnReason,
              lang,
            },
          }
        )
        .then((response) => ({
          ...response.data,
        })),
    getInvoicesLineItemsAsExcel: async (
      accountId: string,
      invoiceNumber: string,
      invoiceType: string,
      dealerLocation: string,
      originalInvoiceNumber?: string,
      nfe?: string,
      originalNfe?: string
    ): Promise<AxiosResponse<BlobResponse>> =>
      axios.getWithRetries(
        `${invoiceServiceUrl}/v1/invoices/${invoiceNumber}/line-items/download/excel`,
        {
          responseType: "blob",
          headers: {
            "account-id": accountId,
          },
          params: {
            invoiceType,
            dealerLocation,
            originalInvoiceNumber,
            nfe,
            originalNfe,
          },
        }
      ),
    getConsolidatedInvoicesLineItemsAsExcel: async (
      request: ConsolidatedInvoicesExcelRequest
    ): Promise<AxiosResponse<BlobResponse>> =>
      axios.postWithRetries(
        `${invoiceServiceUrl}/v1/invoices/consolidated/line-items/download/excel`,
        request,
        {
          responseType: "blob",
          headers: {
            "Content-Type": "application/json",
            Accept: EXCEL_MIME_TYPE,
          },
        }
      ),
  };
};

export default useInvoiceApi;
